import { useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ActivityLog, Integration } from 'dico-types';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import type { MonoIconType } from '@/components/ui/icon';
import { toast } from 'sonner';

function getEventIcon(eventType: string): MonoIconType {
  if (eventType.includes('sync.completed')) return 'CheckCircle';
  if (eventType.includes('sync.started')) return 'RefreshCcw';
  if (eventType.includes('sync.failed')) return 'AlertCircle';
  if (eventType.includes('connected')) return 'CheckCircle';
  if (eventType.includes('disconnected')) return 'XCircle';
  return 'HelpCircle';
}

function getEventIconColor(eventType: string): string {
  if (eventType.includes('sync.completed') || eventType.includes('connected'))
    return 'text-primary';
  if (eventType.includes('sync.started')) return 'text-secondary-foreground';
  if (eventType.includes('sync.failed') || eventType.includes('disconnected'))
    return 'text-destructive';
  return 'text-muted-foreground';
}

function getIntegrationButtonIcon(provider: string): MonoIconType {
  if (provider === 'slack') return 'Slack';
  if (provider === 'discord') return 'Discord';
  if (provider === 'github') return 'GitDiff';
  return 'HelpCircle';
}

function getSourceButtonIcon(provider: string): MonoIconType {
  if (provider === 'github') return 'GitDiff';
  return 'Hash';
}

function deriveSyncTitle(eventType: string, fallback: string): string {
  if (eventType.includes('integration.sync.started')) return 'Sync started';
  if (eventType.includes('integration.sync.completed')) return 'Sync completed';
  if (eventType.includes('integration.sync.failed')) return 'Sync failed';
  return fallback;
}

function deriveLabels(
  log: ActivityLog,
  integrationAccount: Integration | undefined
): { integrationLabel: string | null; sourceLabel: string | null } {
  let integrationLabel: string | null = null;
  let sourceLabel: string | null = null;

  const metadataIntegrationName =
    typeof log.metadata?.integrationName === 'string' ? log.metadata.integrationName : null;
  const metadataSourceName =
    typeof log.metadata?.sourceName === 'string' ? log.metadata.sourceName : null;

  if (metadataIntegrationName) integrationLabel = metadataIntegrationName;

  if (metadataSourceName) {
    sourceLabel = metadataSourceName;
  } else if (integrationLabel) {
    // Back-compat: older logs embedded source as "<team> - <channel>".
    const delim = integrationLabel.lastIndexOf(' - ');
    if (delim !== -1) {
      sourceLabel = integrationLabel.slice(delim + 3) || null;
      integrationLabel = integrationLabel.slice(0, delim) || null;
    }
  }

  // Fallback: parse from eventTitle (current format — no metadata stored).
  if (!integrationLabel || !sourceLabel) {
    const prefixes = ['Now syncing ', 'Successfully synced ', 'Failed to sync '];
    for (const prefix of prefixes) {
      if (!log.eventTitle.startsWith(prefix)) continue;
      const rest = log.eventTitle.slice(prefix.length);
      const delim = rest.lastIndexOf(' - ');
      if (delim !== -1) {
        if (!integrationLabel) integrationLabel = rest.slice(0, delim) || null;
        if (!sourceLabel) sourceLabel = rest.slice(delim + 3) || null;
      } else {
        if (!integrationLabel) integrationLabel = rest || null;
      }
      break;
    }
  }

  // Prefix channel names with # for Slack/Discord.
  if (sourceLabel && integrationAccount?.provider !== 'github') {
    sourceLabel = sourceLabel.startsWith('#') ? sourceLabel : `#${sourceLabel}`;
  }

  return { integrationLabel, sourceLabel };
}

interface ActivityLogItemProps {
  log: ActivityLog;
  integrationAccountProvider: string | null;
  integrationLabel: string | null;
  sourceLabel: string | null;
  /** When true the integration name button is hidden (e.g. already on that page). */
  hideIntegrationButton?: boolean;
  canNavigate: boolean;
  onNavigate: () => void;
}

function ActivityLogItem({
  log,
  integrationAccountProvider,
  integrationLabel,
  sourceLabel,
  hideIntegrationButton = false,
  canNavigate,
  onNavigate
}: ActivityLogItemProps) {
  const IconComponent = getEventIcon(log.eventType);
  const iconColor = getEventIconColor(log.eventType);
  const primaryTitle = deriveSyncTitle(log.eventType, log.eventTitle);

  const showButtons =
    (integrationLabel && integrationAccountProvider && !hideIntegrationButton) || sourceLabel;

  return (
    <div
      className="my-2 flex items-start gap-3 transition-colors"
      role={canNavigate ? 'button' : undefined}
      tabIndex={canNavigate ? 0 : -1}
      onClick={() => {
        if (canNavigate) onNavigate();
      }}
      onKeyDown={(e) => {
        if (!canNavigate) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onNavigate();
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className={`mt-1.5 shrink-0 ${iconColor}`}>
            <MonoIcon type={IconComponent} />
          </div>
          {showButtons ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground text-sm font-medium">{primaryTitle}</span>

              {!hideIntegrationButton && integrationAccountProvider && integrationLabel && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigate();
                  }}
                >
                  <MonoIcon
                    type={getIntegrationButtonIcon(integrationAccountProvider)}
                    className="h-3.5 w-3.5"
                  />
                  {integrationLabel}
                </Button>
              )}

              {sourceLabel && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigate();
                  }}
                >
                  <MonoIcon
                    type={getSourceButtonIcon(integrationAccountProvider ?? '')}
                    className="h-3.5 w-3.5"
                  />
                  {sourceLabel}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-foreground text-sm font-medium">{primaryTitle}</p>
          )}
        </div>
        <div className="ml-2 flex flex-col border-l pl-5">
          <p className="text-muted-foreground text-xs">
            {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
            {log.actorName && ` • By ${log.actorName}`}
          </p>
        </div>
      </div>
    </div>
  );
}

interface ActivityLogListProps {
  logs: ActivityLog[];
  accounts: Integration[];
  workspaceId: string;
  /** When true, the integration name pill is hidden (use on the integration detail page). */
  hideIntegrationButton?: boolean;
  loading?: boolean;
  error?: string | null;
}

export function ActivityLogList({
  logs,
  accounts,
  workspaceId,
  hideIntegrationButton = false,
  loading = false,
  error = null
}: ActivityLogListProps) {
  const navigate = useNavigate();
  const lastToastedErrorRef = useRef<string | null>(null);

  if (error && lastToastedErrorRef.current !== error) {
    lastToastedErrorRef.current = error;
    toast.error(error);
  }

  const enrichedLogs = useMemo(() => {
    return logs.map((log) => {
      const integrationAccount = log.integrationId
        ? accounts.find((a) => a.id === log.integrationId)
        : undefined;

      if (!integrationAccount) {
        return {
          log,
          integrationAccountProvider: null,
          integrationLabel: null,
          sourceLabel: null,
          canNavigate: false,
          onNavigate: () => {}
        };
      }

      const { integrationLabel, sourceLabel } = deriveLabels(log, integrationAccount);

      const onNavigate = () => {
        navigate(
          `/workspace/${workspaceId}/settings/integrations/${integrationAccount.provider}/${integrationAccount.id}`
        );
      };

      return {
        log,
        integrationAccountProvider: integrationAccount.provider,
        integrationLabel,
        sourceLabel,
        canNavigate: true,
        onNavigate
      };
    });
  }, [accounts, logs, navigate, workspaceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <MonoIcon type="Loader2" className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground text-sm">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="divide-border bg-card rounded-lg border px-4 py-2">
      {enrichedLogs.map(({ log, ...derived }) => (
        <ActivityLogItem
          key={log.id}
          log={log}
          {...derived}
          hideIntegrationButton={hideIntegrationButton}
        />
      ))}
    </div>
  );
}
